import { create } from 'zustand';
import type { SearchRequest, SearchResponse, SearchHistory } from '../types/search';
import { searchService } from '../services/searchService';
import { useAppStore } from './useAppStore';

interface SearchState {
  // Search state
  searchRequest: SearchRequest;
  searchResponse: SearchResponse | null;
  isLoading: boolean;
  error: string | null;

  // Search history
  searchHistory: SearchHistory[];
  historyTotalCount: number;
  historyPage: number;
  historyPageSize: number;
  isHistoryLoading: boolean;
  historyError: string | null;

  // Actions
  setSearchRequest: (request: Partial<SearchRequest>) => void;
  search: () => Promise<void>;
  clearSearch: () => void;
  loadSearchHistory: (page?: number) => Promise<void>;
  clearError: () => void;
}

const initialSearchRequest: SearchRequest = {
  query: '',
  startDate: '',
  endDate: '',
  fileType: '',
  author: '',
  tags: [],
  sortBy: 'relevance',
  sortOrder: 'desc',
  page: 1,
  pageSize: 20
};

export const useSearchStore = create<SearchState>((set, get) => ({
  // Initial state
  searchRequest: initialSearchRequest,
  searchResponse: null,
  isLoading: false,
  error: null,
  searchHistory: [],
  historyTotalCount: 0,
  historyPage: 1,
  historyPageSize: 10,
  isHistoryLoading: false,
  historyError: null,

  // Actions
  setSearchRequest: (request) => {
    set((state) => ({
      searchRequest: { ...state.searchRequest, ...request }
    }));
  },

  search: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await searchService.search(get().searchRequest);
      set({ searchResponse: response, isLoading: false });

      // Save search history after successful search
      const user = useAppStore.getState().user;
      if (user && get().searchRequest.query) {
        const filters = {
          startDate: get().searchRequest.startDate || undefined,
          endDate: get().searchRequest.endDate || undefined,
          fileType: get().searchRequest.fileType || undefined,
          author: get().searchRequest.author || undefined,
          tags: get().searchRequest.tags.length > 0 ? get().searchRequest.tags : undefined
        };
        // Remove undefined values
        // @ts-ignore
        Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);

        await searchService.saveSearchHistory(
          user.id,
          get().searchRequest.query,
          Object.keys(filters).length > 0 ? filters : undefined
        );
      }
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Search failed',
        isLoading: false
      });
    }
  },

  clearSearch: () => {
    set({
      searchRequest: initialSearchRequest,
      searchResponse: null,
      error: null
    });
  },

  loadSearchHistory: async (page = 1) => {
    set({ isHistoryLoading: true, historyError: null, historyPage: page });

    try {
      const user = useAppStore.getState().user;
      const userId = user?.id;
      const response = await searchService.getSearchHistory(get().historyPageSize, userId, page);
      set({
        searchHistory: response.history,
        historyTotalCount: response.totalCount,
        isHistoryLoading: false
      });
    } catch (error: any) {
      set({
        historyError: error.response?.data?.message || 'Failed to load search history',
        isHistoryLoading: false,
        searchHistory: []
      });
    }
  },

  clearError: () => {
    set({ error: null, historyError: null });
  }
}));
